import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiActivity,
  FiArrowLeft,
  FiArrowUpRight,
  FiBarChart2,
  FiCopy,
  FiEdit,
  FiGlobe,
  FiLayers,
  FiLink,
  FiMousePointer,
  FiRefreshCw,
  FiSmartphone,
  FiTag,
} from "react-icons/fi";
import "chart.js/auto";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import SideBar from "../../components/sidebar/Sidebar";
import WorldMap from "../../components/WorldMap/WorldMap";
import { useTheme } from "../theme/ThemeProvider";
import { chartTheme, pastelChartColors } from "../theme/chartTheme";
import { getCampaignAnalyticsApi } from "./api";
import CreateCampaignModal from "./CreateCampaignModal";
import UserLoginData from "../Auth/UserLoginData";
import { useListHook } from "../Admin/List/hook";
import { useUpdateStatus } from "../Admin/Modal/ClientsAssign/hook";
import "./campaigns.css";
import "../dashboard/dashboard.css";

const EMPTY_ITEMS = [];
const EMPTY_UTM_STATS = { sources: [], mediums: [], campaigns: [], terms: [], contents: [] };

export const CampaignAnalyticsScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = UserLoginData();
  const { clientList = [] } = useListHook();
  const { defaultData } = useUpdateStatus(isLoggedIn?.response?._id);
  const role = isLoggedIn?.response?.role || "admin";

  const { resolvedTheme } = useTheme();
  const colors = chartTheme(resolvedTheme);
  const isDark = resolvedTheme === "dark";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [utmTab, setUtmTab] = useState("sources"); // 'sources' | 'mediums' | 'contents'

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    const [res, error] = await getCampaignAnalyticsApi(id);
    if (error) {
      const message = error.response?.data?.message || "Failed to load campaign analytics.";
      setLoadError(message);
      toast.error(message);
    } else {
      setData(res);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const campaign = data?.campaign || {};
  const summary = data?.summary || {};
  const links = data?.links_breakdown || [];
  const timeline = data?.timeline || EMPTY_ITEMS;
  const topCountries = data?.countries || EMPTY_ITEMS;
  const utmStats = data?.utm_stats || EMPTY_UTM_STATS;

  const handleCopyLink = async (shortCode) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/${shortCode}`);
      toast.success("Short link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  // Engagement Trend Chart
  const timelineChartData = useMemo(() => {
    const labels = timeline.map((p) => {
      const d = new Date(p.date);
      return Number.isNaN(d.getTime())
        ? p.date
        : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    });
    const values = timeline.map((p) => p.clicks);

    return {
      labels: labels.length ? labels : ["No data"],
      datasets: [
        {
          label: "Campaign Clicks",
          data: values.length ? values : [0],
          fill: "origin",
          borderColor: colors.line,
          borderWidth: 2.5,
          tension: 0.35,
          pointRadius: timeline.length <= 1 ? 5 : 3,
          pointHoverRadius: 6,
          pointBackgroundColor: colors.surface,
          pointBorderWidth: 2,
          backgroundColor: ({ chart }) => {
            const { ctx, chartArea } = chart;
            const fill = isDark ? "168, 205, 235" : "13, 148, 136";
            if (!chartArea) return `rgba(${fill}, 0.12)`;
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, `rgba(${fill}, 0.35)`);
            gradient.addColorStop(0.7, `rgba(${fill}, 0.08)`);
            gradient.addColorStop(1, `rgba(${fill}, 0.01)`);
            return gradient;
          },
        },
      ],
    };
  }, [timeline, colors, isDark]);

  const timelineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: "index" },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: colors.tooltip,
        titleColor: colors.tooltipInk,
        bodyColor: colors.tooltipInk,
        padding: 10,
        cornerRadius: 6,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: colors.muted, maxRotation: 0, maxTicksLimit: 8, font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        border: { display: false },
        grid: { color: colors.grid },
        ticks: { precision: 0, maxTicksLimit: 6, color: colors.muted, padding: 8, font: { size: 11 } },
      },
    },
  };

  // Device Chart Data
  const deviceChartData = useMemo(() => {
    const devices = data?.devices || [];
    const labels = devices.map((d) => (d.device ? d.device.charAt(0).toUpperCase() + d.device.slice(1) : "Desktop"));
    const values = devices.map((d) => d.count);

    return {
      labels: labels.length ? labels : ["No data"],
      datasets: [
        {
          data: values.length ? values : [0],
          backgroundColor: pastelChartColors.slice(0, labels.length || 1),
          borderWidth: 2,
          borderColor: colors.surface,
        },
      ],
    };
  }, [data?.devices, colors.surface]);

  // Browser Chart Data
  const browserChartData = useMemo(() => {
    const browsers = data?.browsers || [];
    const labels = browsers.map((b) => b.browser || "Unknown");
    const values = browsers.map((b) => b.count);

    return {
      labels: labels.length ? labels : ["No data"],
      datasets: [
        {
          data: values.length ? values : [0],
          backgroundColor: pastelChartColors.slice(0, labels.length || 1),
          borderWidth: 2,
          borderColor: colors.surface,
        },
      ],
    };
  }, [data?.browsers, colors.surface]);

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { color: colors.muted, font: { size: 11 }, padding: 12 },
      },
      tooltip: {
        backgroundColor: colors.tooltip,
        titleColor: colors.tooltipInk,
        bodyColor: colors.tooltipInk,
      },
    },
    cutout: "68%",
  };

  // UTM Chart Data
  const activeUtmList = useMemo(() => {
    return utmStats[utmTab] || EMPTY_ITEMS;
  }, [utmTab, utmStats]);

  const utmBarData = useMemo(() => {
    const items = activeUtmList.slice(0, 8);
    const labels = items.map((i) => i.name || "N/A");
    const values = items.map((i) => i.clicks);

    return {
      labels: labels.length ? labels : ["None recorded"],
      datasets: [
        {
          label: "Clicks",
          data: values.length ? values : [0],
          backgroundColor: isDark ? "rgba(168, 205, 235, 0.7)" : "rgba(13, 148, 136, 0.7)",
          borderColor: isDark ? "#a8cdeb" : "#0d9488",
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [activeUtmList, isDark]);

  const utmBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: colors.tooltip,
        titleColor: colors.tooltipInk,
        bodyColor: colors.tooltipInk,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: colors.muted, font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: colors.grid },
        ticks: { color: colors.muted, font: { size: 11 }, precision: 0 },
      },
    },
  };

  const maxCountryCount = useMemo(() => {
    return topCountries.reduce((max, c) => Math.max(max, c.count), 0) || 1;
  }, [topCountries]);

  if (loadError || !data) {
    return (
      <SideBar>
        <section className="campaigns-page">
          <h1>Campaign Analytics</h1>
          {loadError ? (
            <div role="alert">
              <p>{loadError}</p>
              <button type="button" className="btn btn-primary" onClick={loadAnalytics}>Retry</button>
            </div>
          ) : <p role="status">Loading campaign analytics...</p>}
        </section>
      </SideBar>
    );
  }

  return (
    <SideBar>
      <section className="campaigns-page" aria-labelledby="campaign-title">
        {/* Navigation & Header */}
        <div>
          <button
            type="button"
            className="btn btn-sm btn-link text-decoration-none p-0 mb-3 d-inline-flex align-items-center gap-1 text-muted"
            onClick={() => navigate("/campaigns")}
          >
            <FiArrowLeft aria-hidden="true" />
            <span>Back to Campaigns</span>
          </button>

          <header className="campaigns-header">
            <div className="campaigns-header-title">
              <span className="campaigns-header-icon" aria-hidden="true">
                <FiLayers />
              </span>
              <div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h1 id="campaign-title">{campaign.name || "Campaign Analytics"}</h1>
                  {campaign.clientName && (
                    <span className="campaign-card-badge">{campaign.clientName}</span>
                  )}
                  {campaign.utm_campaign && (
                    <span className="badge bg-secondary rounded-pill">
                      <FiTag className="me-1" aria-hidden="true" />
                      {campaign.utm_campaign}
                    </span>
                  )}
                </div>
                {campaign.description ? (
                  <p>{campaign.description}</p>
                ) : (
                  <p>Collective multi-link engagement, traffic attribution, and audience insights.</p>
                )}
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                onClick={loadAnalytics}
                title="Refresh analytics"
              >
                <FiRefreshCw className={loading ? "spin" : ""} aria-hidden="true" />
                <span>Refresh</span>
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                onClick={() => setShowEditModal(true)}
              >
                <FiEdit aria-hidden="true" />
                <span>Manage Links</span>
              </button>
            </div>
          </header>
        </div>

        {/* Collective KPI Cards */}
        <div className="dashboard-kpi-grid">
          <div className="dashboard-kpi-card">
            <div className="dashboard-kpi-info">
              <span className="dashboard-kpi-label">Total Campaign Clicks</span>
              <span className="dashboard-kpi-value">
                {Number(summary.total_clicks || 0).toLocaleString()}
              </span>
              <span className="dashboard-kpi-subtext">Across {summary.total_links || 0} links</span>
            </div>
            <span className="dashboard-kpi-icon" aria-hidden="true">
              <FiMousePointer />
            </span>
          </div>

          <div className="dashboard-kpi-card">
            <div className="dashboard-kpi-info">
              <span className="dashboard-kpi-label">Member Links</span>
              <span className="dashboard-kpi-value">{summary.total_links || 0}</span>
              <span className="dashboard-kpi-subtext">Clubbed destinations</span>
            </div>
            <span className="dashboard-kpi-icon" aria-hidden="true">
              <FiLink />
            </span>
          </div>

          <div className="dashboard-kpi-card">
            <div className="dashboard-kpi-info">
              <span className="dashboard-kpi-label">Top Performer</span>
              <span
                className="dashboard-kpi-value text-truncate d-inline-block"
                style={{ maxWidth: "160px", fontSize: "20px" }}
                title={summary.top_link?.title || "None"}
              >
                {summary.top_link?.title || "No clicks yet"}
              </span>
              <span className="dashboard-kpi-subtext">
                {summary.top_link
                  ? `${Number(summary.top_link.clicks_count || 0).toLocaleString()} clicks (${summary.top_link.traffic_share}%)`
                  : "-"}
              </span>
            </div>
            <span className="dashboard-kpi-icon" aria-hidden="true">
              <FiBarChart2 />
            </span>
          </div>

          <div className="dashboard-kpi-card">
            <div className="dashboard-kpi-info">
              <span className="dashboard-kpi-label">Avg Clicks / Link</span>
              <span className="dashboard-kpi-value">
                {Number(summary.avg_clicks || 0).toLocaleString()}
              </span>
              <span className="dashboard-kpi-subtext">Campaign average</span>
            </div>
            <span className="dashboard-kpi-icon" aria-hidden="true">
              <FiActivity />
            </span>
          </div>
        </div>

        {/* Collective Engagement Over Time Chart */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h2 className="dashboard-card-title">
              <FiActivity aria-hidden="true" />
              <span>Collective Engagement Over Time</span>
            </h2>
            <span className="dashboard-card-badge">
              {timeline.length} recorded day{timeline.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="dashboard-chart-shell">
            <Line
              data={timelineChartData}
              options={timelineChartOptions}
              role="img"
              aria-label="Campaign daily clicks line chart"
            />
          </div>
        </div>

        {/* Member Links Attribution Table */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h2 className="dashboard-card-title">
              <FiLink aria-hidden="true" />
              <span>Member Links Performance & Traffic Share</span>
            </h2>
            <span className="dashboard-card-badge">{links.length} links in campaign</span>
          </div>

          <div className="dashboard-table-shell">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>#</th>
                  <th>Title & Short Code</th>
                  <th>Destination URL</th>
                  <th>UTM Tags</th>
                  <th style={{ textAlign: "right" }}>Clicks</th>
                  <th style={{ width: "160px" }}>Traffic Share</th>
                  <th style={{ width: "80px", textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {links.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4 text-muted">
                      No short links have been added to this campaign yet. Click &quot;Manage Links&quot;
                      above to club links into this campaign.
                    </td>
                  </tr>
                ) : (
                  links.map((link, idx) => (
                    <tr key={link.short_code || idx}>
                      <td className="text-muted fw-bold">{idx + 1}</td>
                      <td>
                        <strong>{link.title || "Untitled link"}</strong>
                        <div className="text-muted small">/{link.short_code}</div>
                      </td>
                      <td>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "240px" }}
                          title={link.url}
                        >
                          {link.url}
                        </a>
                      </td>
                      <td>
                        {Array.isArray(link.utm_source) && link.utm_source[0] ? (
                          <span className="badge bg-light text-dark border me-1">
                            {link.utm_source[0]}
                          </span>
                        ) : null}
                        {Array.isArray(link.utm_medium) && link.utm_medium[0] ? (
                          <span className="badge bg-light text-muted border">
                            {link.utm_medium[0]}
                          </span>
                        ) : null}
                        {(!link.utm_source?.[0] && !link.utm_medium?.[0]) && (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>
                        {Number(link.clicks_count || 0).toLocaleString()}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="dashboard-ranking-bar-wrap flex-grow-1"
                            style={{ height: "6px" }}
                          >
                            <div
                              className="dashboard-ranking-bar"
                              style={{ width: `${link.traffic_share || 0}%` }}
                            />
                          </div>
                          <span className="small fw-bold text-muted" style={{ minWidth: "38px" }}>
                            {link.traffic_share || 0}%
                          </span>
                        </div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div className="d-inline-flex gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary p-1"
                            onClick={() => handleCopyLink(link.short_code)}
                            title="Copy link"
                          >
                            <FiCopy aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary p-1"
                            onClick={() =>
                              navigate(`/list/view/${encodeURIComponent(link.short_code)}`)
                            }
                            title="View link details"
                          >
                            <FiArrowUpRight aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Multi-Link UTM Breakdown Section */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h2 className="dashboard-card-title">
              <FiTag aria-hidden="true" />
              <span>Campaign UTM Attribution</span>
            </h2>
            <div className="dashboard-date-pills">
              <button
                type="button"
                className={`dashboard-date-pill ${utmTab === "sources" ? "active" : ""}`}
                onClick={() => setUtmTab("sources")}
              >
                Sources ({utmStats.sources?.length || 0})
              </button>
              <button
                type="button"
                className={`dashboard-date-pill ${utmTab === "mediums" ? "active" : ""}`}
                onClick={() => setUtmTab("mediums")}
              >
                Mediums ({utmStats.mediums?.length || 0})
              </button>
              <button
                type="button"
                className={`dashboard-date-pill ${utmTab === "campaigns" ? "active" : ""}`}
                onClick={() => setUtmTab("campaigns")}
              >
                Campaigns ({utmStats.campaigns?.length || 0})
              </button>
              <button
                type="button"
                className={`dashboard-date-pill ${utmTab === "terms" ? "active" : ""}`}
                onClick={() => setUtmTab("terms")}
              >
                Terms ({utmStats.terms?.length || 0})
              </button>
              <button
                type="button"
                className={`dashboard-date-pill ${utmTab === "contents" ? "active" : ""}`}
                onClick={() => setUtmTab("contents")}
              >
                Creatives / Content ({utmStats.contents?.length || 0})
              </button>
            </div>
          </div>

          <p className="text-muted small">
            Lifetime clicks grouped by link tags, not individual visit attribution.
            Links with multiple tags contribute to each tag.
          </p>
          <div style={{ height: "240px", width: "100%" }}>
            <Bar
              data={utmBarData}
              options={utmBarOptions}
              role="img"
              aria-label="Campaign UTM attribution bar chart"
            />
          </div>
        </div>

        {/* Geographic Distribution: Map & Top Countries */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h2 className="dashboard-card-title">
              <FiGlobe aria-hidden="true" />
              <span>Audience Geographic Distribution</span>
            </h2>
            <span className="dashboard-card-badge">{topCountries.length} countries recorded</span>
          </div>

          <div className="dashboard-geo-grid">
            <WorldMap countries={topCountries} />

            <div className="dashboard-ranking-list">
              {topCountries.length === 0 ? (
                <div className="dashboard-empty">No country data recorded for this campaign yet.</div>
              ) : (
                topCountries.map((c, idx) => {
                  const pct = Math.round((c.count / maxCountryCount) * 100);
                  return (
                    <div key={c.code || idx} className="dashboard-ranking-item">
                      <div className="dashboard-ranking-row">
                        <span className="dashboard-ranking-name">
                          <span>{c.code}</span> · {c.name || c.code}
                        </span>
                        <span className="dashboard-ranking-count">
                          {c.count.toLocaleString()} clicks
                        </span>
                      </div>
                      <div className="dashboard-ranking-bar-wrap">
                        <div
                          className="dashboard-ranking-bar"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Platform Breakdown: Devices & Browsers */}
        <div className="dashboard-grid-2col">
          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h2 className="dashboard-card-title">
                <FiSmartphone aria-hidden="true" />
                <span>Devices in Campaign</span>
              </h2>
            </div>
            <div className="dashboard-chart-shell" style={{ height: "240px" }}>
              <Doughnut
                data={deviceChartData}
                options={doughnutOptions}
                role="img"
                aria-label="Campaign device distribution donut chart"
              />
            </div>
          </div>

          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h2 className="dashboard-card-title">
                <FiGlobe aria-hidden="true" />
                <span>Browsers in Campaign</span>
              </h2>
            </div>
            <div className="dashboard-chart-shell" style={{ height: "240px" }}>
              <Doughnut
                data={browserChartData}
                options={doughnutOptions}
                role="img"
                aria-label="Campaign browser distribution donut chart"
              />
            </div>
          </div>
        </div>

        {/* Edit / Manage Links Modal */}
        <CreateCampaignModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          onSuccess={loadAnalytics}
          campaignToEdit={campaign}
          availableClients={role === "admin" ? clientList : (defaultData || [])}
          currentRole={role}
          currentClientId={campaign.clientId}
        />
      </section>
    </SideBar>
  );
};

export default CampaignAnalyticsScreen;
